/**
 * EditableCSVTable Component
 *
 * UI/Aesthetics: Editable data table with status indicators, inline editing, and retry functionality
 * Functions:
 * - Displays CSV data in a table format with sticky headers
 * - Inline editing of cell values with click-to-edit functionality
 * - Status indicators for upload progress (pending, uploading, success, failed)
 * - Retry functionality for failed uploads
 * - Row highlighting based on upload status
 * Previous: Used MUI Table + TableCell + TextField + IconButton + Tooltip + CircularProgress + MUI icons + MD components with complex sx styling
 * Now: Uses shadcn Table + Input + Button + Tooltip + Spinner + Lucide icons with Tailwind classes
 */

import React, { useState, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Spinner } from '@/components/ui/spinner';
import {
  CheckCircleIcon,
  WarningCircleIcon,
  ClockIcon,
  ArrowCounterClockwiseIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { UploadResult } from './BulkCSVUpload';

interface EditableCSVTableProps {
  data: Record<string, unknown>[];
  columns: string[];
  uploadResults: UploadResult[];
  onDataEdit: (rowIndex: number, field: string, value: unknown) => void;
  onRetryRow: (index: number) => void;
  isUploading: boolean;
}

const StatusIcon: React.FC<{ result: UploadResult; onRetry: () => void }> = ({
  result,
  onRetry,
}) => {
  const getStatusIcon = () => {
    switch (result.status) {
      case 'pending':
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <ClockIcon className="h-5 w-5 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Pending upload</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      case 'uploading':
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Spinner size="sm" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Uploading...</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      case 'success':
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <CheckCircleIcon className="h-5 w-5 text-green-600" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Upload successful</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      case 'failed':
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1">
                  <WarningCircleIcon className="h-5 w-5 text-red-600" />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onRetry}
                    className="h-6 w-6 p-0 hover:bg-gray-100"
                  >
                    <ArrowCounterClockwiseIcon className="h-3 w-3" />
                  </Button>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{result.error || 'Upload failed'} - Click to retry</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      default:
        return null;
    }
  };

  return <div>{getStatusIcon()}</div>;
};

const EditableCell: React.FC<{
  value: unknown;
  rowIndex: number;
  field: string;
  onEdit: (rowIndex: number, field: string, value: unknown) => void;
  disabled: boolean;
}> = ({ value, rowIndex, field, onEdit, disabled }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(String(value || ''));

  const handleSave = useCallback(() => {
    onEdit(rowIndex, field, editValue);
    setIsEditing(false);
  }, [rowIndex, field, editValue, onEdit]);

  const handleCancel = useCallback(() => {
    setEditValue(String(value || ''));
    setIsEditing(false);
  }, [value]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSave();
      } else if (e.key === 'Escape') {
        handleCancel();
      }
    },
    [handleSave, handleCancel],
  );

  if (isEditing) {
    return (
      <Input
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyPress}
        autoFocus
        className="text-sm border-none shadow-none p-0 h-auto focus-visible:ring-0"
      />
    );
  }

  return (
    <div
      onClick={() => !disabled && setIsEditing(true)}
      className={cn(
        'py-2 min-h-[20px] rounded',
        disabled ? 'cursor-default' : 'cursor-pointer hover:bg-gray-50',
      )}
    >
      <span
        className={cn(
          'text-sm',
          !value ? 'italic text-muted-foreground' : 'text-foreground',
        )}
      >
        {String(value) || 'Click to edit'}
      </span>
    </div>
  );
};

const EditableCSVTable: React.FC<EditableCSVTableProps> = ({
  data,
  columns,
  uploadResults,
  onDataEdit,
  onRetryRow,
  isUploading,
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-muted-foreground">No data to display</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="max-h-[60vh] overflow-auto">
        <Table>
          <TableHeader className="sticky top-0 bg-background">
            <TableRow>
              <TableHead className="min-w-[80px]">
                <span className="text-xs font-medium text-muted-foreground uppercase">
                  Status
                </span>
              </TableHead>
              <TableHead className="min-w-[200px]">
                <span className="text-xs font-medium text-muted-foreground uppercase">
                  Upload Status
                </span>
              </TableHead>
              {columns.map((column) => (
                <TableHead key={column} className="min-w-[150px]">
                  <span className="text-xs font-medium text-muted-foreground uppercase">
                    {column}
                  </span>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row, rowIndex) => {
              const result = uploadResults[rowIndex];
              const isRowUploading = result?.status === 'uploading';
              const isRowSuccess = result?.status === 'success';

              return (
                <TableRow
                  key={rowIndex}
                  className={cn(
                    'hover:bg-muted/50',
                    isRowSuccess && 'bg-green-50 hover:bg-green-100',
                    result?.status === 'failed' && 'bg-red-50 hover:bg-red-100',
                  )}
                >
                  <TableCell>
                    {result && (
                      <StatusIcon
                        result={result}
                        onRetry={() => onRetryRow(rowIndex)}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {result && (
                      <div className="text-sm">
                        <div
                          className={cn(
                            'font-medium capitalize',
                            result.status === 'success' && 'text-green-700',
                            result.status === 'failed' && 'text-red-700',
                            result.status === 'pending' && 'text-gray-600',
                            result.status === 'uploading' && 'text-blue-600',
                          )}
                        >
                          {result.status}
                        </div>
                        {result.error && (
                          <div className="text-xs text-red-600 mt-1 break-words">
                            {result.error}
                          </div>
                        )}
                      </div>
                    )}
                  </TableCell>
                  {columns.map((column) => (
                    <TableCell key={column}>
                      <EditableCell
                        value={row[column]}
                        rowIndex={rowIndex}
                        field={column}
                        onEdit={onDataEdit}
                        disabled={isUploading || isRowUploading || isRowSuccess}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default EditableCSVTable;
